import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAllLeadsRequest,
  updateLeadRequest,
} from '../../../redux/Required/requiredSlice';
import TableCompo from '../Table/TableCompo';
import { useNavigate } from 'react-router-dom';
import { CiEdit } from 'react-icons/ci';
import {
  Box,
  Button,
  Stack,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Chip,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { apiRequest } from '../../../redux/Reports/reportsSlice';
import useCsvConverter from '../../../hooks/convertToCSV';
import {
  createJewellerRequest,
  resetCreatedJewellerId,
} from '../../../redux/JewellerList/JewellerSlice';
import { canShowPermission } from '../Permissions/permissionObject';

const LeadList = ({ refresh, onEdit }) => {
  // added onEdit prop
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { convertToCsv } = useCsvConverter();
  const { leadList = [], total = 0 } = useSelector((state) => state.lead || {});
  const createdJewellerId = useSelector(
    (state) => state.jeweller.createdJewellerId,
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [currentPage, setCurrentPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [appliedDateRange, setAppliedDateRange] = useState(null);

  const [openJewellerDialog, setOpenJewellerDialog] = useState(false);
  const [selectedLeadForJeweller, setSelectedLeadForJeweller] = useState(null);
  const [jewellerFormData, setJewellerFormData] = useState({
    gst: '',
    package_name: '',
    comment: '',
  });
  const [jewellerErrors, setJewellerErrors] = useState({});
  const [openConversionDialog, setOpenConversionDialog] = useState(false);
  const [selectedLeadForConversion, setSelectedLeadForConversion] =
    useState(null);

  const formatDateTimeRange = (date, type) => {
    if (!date) return null;
    const d = new Date(date);
    if (type === 'start') {
      d.setHours(0, 0, 0, 0);
      return d.toISOString();
    }
    d.setDate(d.getDate() + 1);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  };

  const buildFetchPayload = () => {
    const payload = { page: currentPage + 1, limit, search };
    if (appliedDateRange?.start && appliedDateRange?.end) {
      payload.startDate = formatDateTimeRange(appliedDateRange.start, 'start');
      payload.endDate = formatDateTimeRange(appliedDateRange.end, 'end');
    }
    return payload;
  };

  const refreshLeadList = () => {
    dispatch(getAllLeadsRequest(buildFetchPayload()));
  };

  useEffect(() => {
    dispatch(getAllLeadsRequest(buildFetchPayload())); //trigger get data of leads
  }, [refresh]);

  const handleGetDetails = () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates.');
      return;
    }
    if (startDate > endDate) {
      toast.error('Start Date cannot be after End Date');
      return;
    }

    setCurrentPage(0);
    setAppliedDateRange({ start: startDate, end: endDate });
    dispatch(
      getAllLeadsRequest({
        page: 1,
        limit,
        search,
        startDate: formatDateTimeRange(startDate, 'start'),
        endDate: formatDateTimeRange(endDate, 'end'),
      }),
    );
  };

  const handleTableChange = (action, payload) => {
    const dateParams =
      appliedDateRange?.start && appliedDateRange?.end
        ? {
            startDate: formatDateTimeRange(appliedDateRange.start, 'start'),
            endDate: formatDateTimeRange(appliedDateRange.end, 'end'),
          }
        : {};

    switch (action) {
      case 'changePage':
        setCurrentPage(payload.page);
        dispatch(
          getAllLeadsRequest({
            page: payload.page + 1,
            limit,
            search,
            ...dateParams,
          }),
        );
        break;

      case 'changeRowsPerPage':
        setLimit(payload.rowsPerPage);
        setCurrentPage(0);
        dispatch(
          getAllLeadsRequest({
            page: 1,
            limit: payload.rowsPerPage,
            search,
            ...dateParams,
          }),
        );
        break;

      case 'search':
        const searched = payload.searchText || '';
        setSearch(searched);
        setCurrentPage(0);
        dispatch(
          getAllLeadsRequest({
            page: 1,
            limit,
            search: searched,
            ...dateParams,
          }),
        );
        break;

      default:
        break;
    }
  };

  const resetJewellerForm = () => {
    setJewellerFormData({ gst: '', package_name: '', comment: '' });
    setJewellerErrors({});
    setOpenJewellerDialog(false);
    setSelectedLeadForJeweller(null);
  };

  const handleOpenJewellerDialog = (row) => {
    setSelectedLeadForJeweller(row);
    setOpenJewellerDialog(true);
  };

  const handleCreateJewellerConfirm = () => {
    const tempErrors = {};
    if (!jewellerFormData.gst?.trim())
      tempErrors.gst = 'GST Number is required';
    if (!jewellerFormData.package_name?.trim())
      tempErrors.package_name = 'Package Name (Android) is required';

    if (Object.keys(tempErrors).length > 0) {
      setJewellerErrors(tempErrors);
      Object.values(tempErrors).forEach((msg) => toast.error(msg));
      return;
    }
    const lead = selectedLeadForJeweller;

    const jewellerPayload = {
      jewellerName: lead?.jewellerName,
      email: lead?.email || '',
      phone: lead?.phone,
      countryCode: lead?.countryCode,
      identity: 'Gst',
      cluster: 'cluster-1',
      package_name: jewellerFormData.package_name,
      package_name_ios: '',
      address: [
        {
          address1: lead?.city || '',
          address2: lead?.address2 || '',
          address3: lead?.address3 || '',
          Area: lead?.area || '',
          city: lead?.city || '',
          state: lead?.state || '',
          pincode: lead?.pincode || '',
          gst: jewellerFormData.gst,
        },
      ],
      RM: Array.isArray(lead?.rmId)
        ? lead.rmId
        : lead?.rmId
        ? [lead.rmId]
        : [],
      leadId: lead?._id ? lead._id : null,
    };
    const trimmedComment = jewellerFormData.comment?.trim();
    if (trimmedComment) {
      jewellerPayload.comment = trimmedComment;
    }
    dispatch(createJewellerRequest(jewellerPayload));
    // toast.success('Jeweller creation request submitted!');
    // dialog stays open until createdJewellerId comes back via the effect above
  };

  const [permissions, setPermissions] = useState({});

  useEffect(() => {
    const storedPermissions = localStorage.getItem('userPermissions');
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (e) {
        console.error('Invalid permissions JSON', e);
      }
    }
  }, []);
  const handleOpenConversionDialog = (row) => {
    setSelectedLeadForConversion(row);
    setOpenConversionDialog(true);
  };

  const handleCloseConversionDialog = () => {
    setOpenConversionDialog(false);
    setSelectedLeadForConversion(null);
  };

  const getLeadConversion = (row) =>
    String(row?.leadConversion || 'NO').toUpperCase() === 'YES' ? 'YES' : 'NO';

  const openJewellerCreation = (row) => {
    navigate('/CreateJwellerList', {
      state: { openJewellerForm: true, lead: row },
    });
  };

  const handleConfirmConversion = () => {
    if (!selectedLeadForConversion?._id) return;
    const nextStatus = getLeadConversion(selectedLeadForConversion) === 'YES' ? 'NO' : 'YES';
    dispatch(
      updateLeadRequest({
        id: selectedLeadForConversion._id,
        leadConversion: nextStatus,
      }),
    );
    handleCloseConversionDialog();
    if (nextStatus === 'YES' && !selectedLeadForConversion.jeweller) {
      openJewellerCreation(selectedLeadForConversion);
      return;
    }
    toast.success(`Lead conversion updated to ${nextStatus}`);
  };

  const canView = canShowPermission(permissions, 'jeweller', 'view');

  const columns = [
    { field: 'id', headerName: 'ID', hide: true, hideInExcel: true },
    {
      field: 'createdAt',
      headerName: 'Date',
      render: (val, row) => {
        if (!row?.createdAt) return 'N.A';

        const formattedDate = new Date(row.createdAt).toLocaleString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        });

        return <span title={formattedDate}>{formattedDate}</span>;
      },
    },
    { field: 'jewellerName', headerName: "Jeweller's Shop Name", width: 180 },
    { field: 'contactName', headerName: 'Contact Person Name', width: 180 },
    { field: 'designation', headerName: 'Designation', width: 180 },
    { field: 'phone', headerName: 'Phone', width: 150 },
    { field: 'email', headerName: 'Email', width: 200 },
    { field: 'area', headerName: 'Area', width: 150 },
    { field: 'city', headerName: 'City', width: 150 },
    { field: 'state', headerName: 'State', width: 150 },
    { field: 'pincode', headerName: 'Pincode', width: 120 },
    { field: 'rm', headerName: 'Support Manager', width: 220 },
    { field: 'accountOwner', headerName: 'Account Owner', width: 220 },
    { field: 'source', headerName: 'Source', width: 140 },
    { field: 'createdBy', headerName: 'Created By', width: 180 },
    {
      field: 'Details',
      headerName: 'Details',
      render: (val, row) => (
        <button
          style={{
            backgroundColor: '#ffc107',
            color: '#000',
            border: 'none',
            padding: '5px 10px',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
          onClick={() => {
            const fullLead = leadList.find((l) => l._id === row._id);
            navigate(`/CreateFollowup`, {
              state: { lead: fullLead || row, type: 'lead' },
            });
          }}
        >
          View
        </button>
      ),
    },
    {
      field: 'edit',
      headerName: 'Edit',
      render: (val, row) => {
        const fullLead = leadList.find((l) => l._id === row._id);
        return (
          <CiEdit
            style={{ cursor: 'pointer' }}
            onClick={() => onEdit && onEdit(fullLead || row)} // pass the row (which contains _id)
          />
        );
      },
      hideInExcel: true,
    },
    {
      field: 'leadConversion',
      headerName: 'Lead Conversion',
      width: 160,
      render: (val, row) => {
        const showJewellerConvert = !row?.jeweller;
        const current = showJewellerConvert ? 'NO' : 'YES';
        const isYes = current === 'YES';
        return (
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              if (isYes) return;
              openJewellerCreation(row);
            }}
            sx={{
              minWidth: 64,
              height: 26,
              px: 1.5,
              fontWeight: 700,
              fontSize: 11,
              textTransform: 'none',
              boxShadow: 'none',
              color: isYes ? '#047857' : '#6B7280',
              backgroundColor: isYes ? '#D1FAE5' : '#F3F4F6',
              '&:hover': {
                boxShadow: 'none',
                backgroundColor: isYes ? '#A7F3D0' : '#E5E7EB',
              },
            }}
          >
            {current}
          </Button>
        );
      },
    },
    ...(canView
      ? [
          {
            field: 'convertToJeweller',
            headerName: 'Jeweller Converted',
            width: 180,
            hideInExcel: true,
            render: (val, row) =>
              !row?.jeweller ? (
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    textTransform: 'none',
                    background:
                      'linear-gradient(90deg, #7028DA 0%, #9E3ED7 76.93%)',
                  }}
                  onClick={() => {
                    openJewellerCreation(row);
                  }}
                >
                  Jeweller Converted
                </Button>
              ) : null,
          },
        ]
      : []),
  ];

  const triggerCsvDownload = (csvText, filename) => {
    const blob = new Blob([csvText], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();

    window.URL.revokeObjectURL(url);
  };

  const handleDownloadLeadsCsv = () => {
    const query = {};
    if (appliedDateRange?.start && appliedDateRange?.end) {
      query.startDate = String(
        new Date(
          formatDateTimeRange(appliedDateRange.start, 'start'),
        ).getTime(),
      );
      query.endDate = String(
        new Date(formatDateTimeRange(appliedDateRange.end, 'end')).getTime(),
      );
    }

    setExporting(true);

    dispatch(
      apiRequest({
        method: 'GET',
        endpoint: '/api/v1/lead/getLeadsCsv',
        query,
        callback: (response) => {
          setExporting(false);

          if (!response?.success) {
            toast.error(response?.message || 'Failed to export leads');
            return;
          }

          const csv = convertToCsv(response?.data || []);
          triggerCsvDownload(csv, 'leads.csv');
        },
      }),
    );
  };

  return (
    <Box>
      {/* <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box
          display="flex"
          flexDirection={isMobile ? 'column' : 'row'}
          gap={1.5}
          px={2}
          py={1}
        >
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={setStartDate}
            maxDate={endDate || new Date()}
            slotProps={{
              textField: {
                size: 'small',
                sx: {
                  minWidth: 160,
                  '& .MuiInputBase-root': { height: 36, fontSize: 13 },
                  '& .MuiInputLabel-root': { fontSize: 13 },
                },
              },
            }}
          />

          <DatePicker
            label="End Date"
            value={endDate}
            onChange={setEndDate}
            minDate={startDate}
            slotProps={{
              textField: {
                size: 'small',
                sx: {
                  minWidth: 160,
                  '& .MuiInputBase-root': { height: 36, fontSize: 13 },
                  '& .MuiInputLabel-root': { fontSize: 13 },
                },
              },
            }}
          />
          <Button
            variant="outlined"
            fullWidth={isMobile}
            sx={{
              height: 36,
              minWidth: 120,
              fontSize: 13,
              textTransform: 'none',
              px: 2,
              whiteSpace: 'nowrap',
            }}
            onClick={handleGetDetails}
          >
            Get Details
          </Button>
        </Box>
      </LocalizationProvider> */}

      <TableCompo
        columns={columns}
        data={leadList.map((row, index) => ({
          id: index + 1,
          createdAt: row.createdAt,
          jewellerName: row.jewellerName || '',
          contactName: row.contactName || '',
          designation: row.designation || '',
          phone: row.contactNumber || '',
          email: row.email || '',
          area: row.area || '',
          city: row.city || '',
          state: row.state || '',
          pincode: row.pincode || '',
          rm: (
            Array.isArray(row.assignedUser)
              ? row.assignedUser
              : row.assignedUser
              ? [row.assignedUser]
              : []
          )
            .map((user) => user?.name)
            .filter(Boolean)
            .join(', ') || '-',
          rmId: (
            Array.isArray(row.assignedUser)
              ? row.assignedUser
              : row.assignedUser
              ? [row.assignedUser]
              : []
          )
            .map((user) => user?._id)
            .filter(Boolean),
          accountOwner: (
            Array.isArray(row.accountOwnerUsers)
              ? row.accountOwnerUsers
              : row.accountOwnerUsers
              ? [row.accountOwnerUsers]
              : []
          )
            .map((user) => user?.name)
            .filter(Boolean)
            .join(', ') || '-',
          countryCode: row.countryCode || '',
          jeweller: row.jeweller || '',
          source: row.source || '',
          jewellerConverted: row.jewellerConverted || 'NO',
          leadConversion: row.leadConversion || 'NO',
          createdBy: row.createdByUser?.name || '',
          _id: row._id,
        }))}
        totalCount={total}
        page={currentPage}
        limit={limit}
        onTableChange={handleTableChange}
        extraButtons={
          <Button
            size="sm"
            variant="outlined"
            disabled={exporting}
            onClick={handleDownloadLeadsCsv}
          >
            {exporting ? 'Exporting...' : 'Download CSV'}
          </Button>
        }
      />
      {/* Convert Lead to Jeweller Dialog */}
      <Dialog
        open={openJewellerDialog}
        onClose={resetJewellerForm}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Jeweller</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please confirm the required details below to create a jeweller for
            this lead.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', width: '100%', mb: 2 }}>
            {[
              ['Jeweller Name', selectedLeadForJeweller?.jewellerName],
              ['Contact Name', selectedLeadForJeweller?.contactName],
              ['Phone', selectedLeadForJeweller?.phone],
              ['Area', selectedLeadForJeweller?.area || '-'],
              ['City', selectedLeadForJeweller?.city || '-'],
              ['State', selectedLeadForJeweller?.state || '-'],
              ['Pincode', selectedLeadForJeweller?.pincode || '-'],
            ].map(([label, val], idx) => (
              <Box key={idx} sx={{ width: '50%', mb: 1 }}>
                <Typography fontWeight={600} component="span">
                  {label}:{' '}
                </Typography>
                <Typography component="span">{val}</Typography>
              </Box>
            ))}
          </Box>
          <TextField
            fullWidth
            size="small"
            label="GST Number"
            required
            value={jewellerFormData.gst}
            onChange={(e) =>
              setJewellerFormData((prev) => ({ ...prev, gst: e.target.value }))
            }
            error={!!jewellerErrors.gst}
            helperText={jewellerErrors.gst}
            sx={{ mt: 1 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Package Name (Android)"
            required
            value={jewellerFormData.package_name}
            onChange={(e) =>
              setJewellerFormData((prev) => ({
                ...prev,
                package_name: e.target.value,
              }))
            }
            error={!!jewellerErrors.package_name}
            helperText={jewellerErrors.package_name}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={3}
            label="Comment"
            placeholder="Add a comment (optional)"
            value={jewellerFormData.comment}
            onChange={(e) =>
              setJewellerFormData((prev) => ({
                ...prev,
                comment: e.target.value,
              }))
            }
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            onClick={resetJewellerForm}
            sx={{
              textTransform: 'none',
              background: 'linear-gradient(90deg, #7028DA 0%, #9E3ED7 76.93%)',
              WebkitBackgroundClip: 'text',
              fontWeight: '700',
              WebkitTextFillColor: 'transparent',
            }}
          >
            No
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={handleCreateJewellerConfirm}
            sx={{
              textTransform: 'none',
              background: 'linear-gradient(90deg, #7028DA 0%, #9E3ED7 76.93%)',
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openConversionDialog}
        onClose={handleCloseConversionDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Change Lead Conversion</DialogTitle>
        <DialogContent>
          <Typography>
            Do you want to change status to{' '}
            <b>
              {getLeadConversion(selectedLeadForConversion) === 'YES'
                ? 'NO'
                : 'YES'}
            </b>
            {selectedLeadForConversion?.jewellerName
              ? ` for ${selectedLeadForConversion.jewellerName}`
              : ''}
            ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            onClick={handleCloseConversionDialog}
            sx={{
              textTransform: 'none',
              background: 'linear-gradient(90deg, #7028DA 0%, #9E3ED7 76.93%)',
              WebkitBackgroundClip: 'text',
              fontWeight: '700',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={handleConfirmConversion}
            sx={{
              textTransform: 'none',
              background: 'linear-gradient(90deg, #7028DA 0%, #9E3ED7 76.93%)',
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeadList;
